
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Box, Button, IconButton, Typography, useTheme, useMediaQuery, Tooltip, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GroupIcon from '@mui/icons-material/Group';
import { useAuth } from '../contexts/AuthContext';

const navItems = [
  { label: 'Summary', path: '/trader-summary', icon: <DashboardIcon /> },
  { label: 'Trade', path: '/trade', icon: <ShowChartIcon /> },
  { label: 'User Management', path: '/usermanagement', icon: <GroupIcon />, adminOnly: true },
];

const TraderDashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const path = window.location.pathname;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const { user } = useAuth();

  const drawer = (
    <Box sx={{
      width: sidebarExpanded ? 220 : 72,
      background: 'linear-gradient(180deg, #162447 0%, #1f4068 100%)',
      color: '#fff',
      height: '100%',
      pt: 3,
      transition: 'width 0.3s',
      boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: sidebarExpanded ? 'space-between' : 'center', px: 2, mb: 2 }}>
        {sidebarExpanded && (
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            Dashboard
          </Typography>
        )}
        <IconButton
          onClick={() => setSidebarExpanded(exp => !exp)}
          size="small"
          sx={{
            color: sidebarExpanded ? '#fff' : theme => theme.palette.secondary.main,
            ml: sidebarExpanded ? 0 : 0,
            transition: 'margin 0.3s',
          }}
        >
          {sidebarExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 1, mx: sidebarExpanded ? 2 : 1 }} />
      {navItems.map(item => (
        (!item.adminOnly || (user && user.userType === 'admin')) && (
          <Tooltip key={item.path} title={!sidebarExpanded ? item.label : ''} placement="right" arrow disableInteractive>
            <Button
              fullWidth
              variant={path === item.path ? 'contained' : 'text'}
              color={path === item.path ? 'secondary' : 'inherit'}
              sx={{
                justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                px: sidebarExpanded ? 3 : 0,
                py: 2,
                fontWeight: 700,
                fontSize: 16,
                borderRadius: 2,
                mb: 1,
                background: path === item.path ? 'rgba(22,36,71,0.8)' : 'transparent',
                color: '#fff',
                minHeight: 56,
                '&:hover': {
                  background: 'rgba(31,64,104,0.6)',
                },
                transition: 'all 0.3s',
              }}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setDrawerOpen(false);
              }}
              startIcon={item.icon}
            >
              {sidebarExpanded && item.label}
            </Button>
          </Tooltip>
        )
      ))}
    </Box>
  );

  // Height of the Navbar (sticky) is 64px (default MUI AppBar height), adjust if your Navbar is taller
  const NAVBAR_HEIGHT = 64;
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar/Drawer */}
      {isMobile ? (
        <>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ position: 'fixed', top: NAVBAR_HEIGHT + 8, left: 16, zIndex: 1300, bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
            size="large"
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: { bgcolor: 'primary.main', color: '#fff', width: 220, mt: `${NAVBAR_HEIGHT}px` } }}
            sx={{ mt: `${NAVBAR_HEIGHT}px` }}
          >
            {drawer}
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          open
          PaperProps={{
            sx: {
              bgcolor: sidebarExpanded ? 'primary.main' : 'transparent',
              color: '#fff',
              width: sidebarExpanded ? 220 : 72,
              border: 'none',
              mt: `${NAVBAR_HEIGHT}px`,
              boxShadow: 'none',
              transition: 'width 0.3s, background 0.3s',
            }
          }}
        >
          {drawer}
        </Drawer>
      )}
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          p: { xs: 2, sm: 4, md: 6 },
          minWidth: 0,
          ml: { md: sidebarExpanded ? '220px' : '72px', xs: 0 },
          transition: 'margin-left 0.3s',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default TraderDashboardLayout;
